import type { PropsWithChildren } from "react"
import React from "react"
import type { StyleProps } from "@mallardbay/lib-react-components"
import {
    Drawer as DrawerOg,
    DrawerBody,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
} from "@chakra-ui/react"
import { componentStyles, useColors } from "@mallardbay/lib-react-components"

import { Z_INDICES } from "~config/constants"

export enum DrawerPlacement {
    Bottom = "bottom",
    Top = "top",
    Left = "left",
    Right = "right",
    Start = "start",
    End = "end",
}

export enum DrawerSize {
    ExtraSmall = "xs",
    Small = "sm",
    Medium = "md",
    Large = "lg",
    ExtraLarge = "xl",
    Full = "full",
}

interface Props extends PropsWithChildren {
    readonly style?: StyleProps
    readonly isOpen?: boolean
    readonly onClose: () => void
    readonly placement?: DrawerPlacement
    readonly size?: DrawerSize
    readonly shouldHideCloseButton?: boolean
}

export default function Drawer({
    style,
    isOpen = false,
    onClose,
    placement,
    size,
    children,
    shouldHideCloseButton,
}: Props) {
    const isFullSize = size === DrawerSize.Full
    const styles = useStyles({ isFullSize })

    return (
        <DrawerOg
            isOpen={isOpen}
            onClose={onClose}
            placement={placement}
            size={size}
        >
            <DrawerOverlay style={styles.overlay}>
                <DrawerContent sx={styles.content}>
                    {!shouldHideCloseButton && <DrawerCloseButton />}
                    <DrawerBody sx={{ ...styles.body, ...style }}>
                        {children}
                    </DrawerBody>
                </DrawerContent>
            </DrawerOverlay>
        </DrawerOg>
    )
}

function useStyles({ isFullSize }: { isFullSize: boolean }) {
    const colors = useColors()

    return componentStyles({
        overlay: {
            zIndex: Z_INDICES.DRAWER,
        },
        content: {
            borderTopRadius: isFullSize ? "none" : "xl",
            backgroundColor: colors.bgColor,
        },
        body: {
            padding: "10px 0 0 0",
        },
    })
}
